import React, { useState, useEffect } from "react";
import {
  Divider,
  Typography,
  TextField,
  makeStyles,
  FormControl,
  FormControlLabel,
  Select as Mselect,
  useMediaQuery,
  Switch,
} from "@material-ui/core";
import moment from "moment";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";

import { Card, CardContent, CardActions } from "../../components/Card";
import Grid from "../../components/Grid";
import divisions from "../../divisions.json";
import Button from "../../components/Button";
import LoadingModal from "../../components/LoadingModal";
import { DateInput, Select } from "../../components/Form";
import { useAlert } from "../../components/Alert";

const REGISTER = gql`
  mutation register($userInput: UserInput!, $console: Boolean) {
    register(userInput: $userInput, console: $console) {
      id
    }
  }
`;
const GET_USER = gql`
  query user($id: Int) {
    user(id: $id) {
      id
      username
      fullName
      gender
      mobile
      phone
      fax
      email
      address {
        division
        subdivision
        address
      }
      branch
      department
      position
      birthday
      identity
      enabled
    }
  }
`;
const UPDATE_USER = gql`
  mutation updateUser($userInput: UserInput!, $console: Boolean) {
    updateUser(userInput: $userInput, console: $console)
  }
`;

const CHANGEUSERENABLED = gql`
  mutation changeUserEnabled($id: Int!, $enabled: Boolean!) {
    changeUserEnabled(id: $id, enabled: $enabled)
  }
`;

const RESETPASSWORD = gql`
  mutation resetPassword($id: Int, $password: String!) {
    resetPassword(id: $id, password: $password)
  }
`;

export default function HRmanageForm({ id }) {
  const Alert = useAlert();
  let city = Object.keys(divisions);
  const [selectDistrict, setSelectDistrict] = useState();
  const [enabled, setEnabled] = useState(false);
  const isPhone = useMediaQuery("(max-width:376px)");
  const genderArray = [
    { label: "男", value: "MALE" },
    { label: "女", value: "FEMALE" },
  ];
  const { control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      fullName: "",
      username: "",
      password: "",
      gender: "",
      branch: "",
      department: "",
      position: "",
      fax: "",
      mobile: "",
      phone: "",
      email: "",
      birthday: null,
      address: "",
      district: "",
      subdistrict: "",
      joinTime: null,
    },
  });
  const useStyles = makeStyles((theme) => ({
    select: {
      backgroundColor: "white",
      width: isPhone ? "auto" : "130px",
      minWidth: "155px",
      height: "22px",
      padding: "7px 24px 7px 10px",
      border: "1px solid black",
      borderRadius: "5px",
      borderColor: "#bdbdbd",
      "&:focus": {
        backgroundColor: "white",
        borderRadius: "5px",
      },
    },
    input: {
      backgroundColor: "white",
      borderRadius: "4px",
      marginBottom: "20px",
    },
  }));
  const classes = useStyles();
  const [userRegister, { loading: registerLoading }] = useMutation(REGISTER, {
    onCompleted({ register }) {
      if (register) {
        return Alert.notice("新增成功！", () => {});
      } else {
        return Alert.notice("新增失敗，請重新嘗試！");
      }
    },
    onError(error) {
      return Alert.notice(error.message.replace("GraphQL error: ", ""));
    },
  });
  const [getUser, { data: userData, loading }] = useLazyQuery(GET_USER, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted({ user }) {
      if (user) {
        setValue("username", user.username);
        setValue("fullName", user.fullName);
        const gender = genderArray.find((item) => item.value === user.gender);
        setValue("gender", gender);
        setValue("mobile", user.mobile);
        setValue("phone", user.phone);
        setValue("fax", user.fax);
        setValue("branch", user.branch);
        setValue("department", user.department);
        setValue("position", user.position);
        setValue("email", user.email);
        if (Boolean(user.address)) {
          setValue("district", user.address.division);
          setValue("subdistrict", user.address.subdivision);
          setValue("address", user.address.address);
          setSelectDistrict(divisions[user.address.division].subdivisions);
        }
        setValue("birthday", user.birthday);
        setEnabled(user.enabled);
      }
    },
    onError(error) {
      return Alert.notice(error.message.replace("GraphQL error: ", ""));
    },
  });
  const [updateUser, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER,
    {
      onCompleted({ updateUser }) {
        if (updateUser) {
          getUser({ variables: { id } });
          return Alert.notice("儲存成功！");
        } else {
          return Alert.notice("儲存失敗，請重新嘗試！");
        }
      },
      onError(error) {
        return Alert.notice(error.message.replace("GraphQL error: ", ""));
      },
    }
  );

  const [changeUserEnabled, { loading: changeUserEnabledLoading }] =
    useMutation(CHANGEUSERENABLED, {
      onCompleted({ changeUserEnabled }) {
        if (changeUserEnabled) {
          return Alert.notice("變更成功！");
        } else {
          return Alert.notice("變更失敗，請重新嘗試！");
        }
      },
      onError(error) {
        return Alert.notice(error.message.replace("GraphQL error: ", ""));
      },
    });

  const [resetPassword, { loading: resetPasswordLoading }] = useMutation(
    RESETPASSWORD,
    {
      onCompleted({ resetPassword }) {
        if (resetPassword) {
          Alert.alert("", "重設成功", [
            {
              text: "確定",
              onPress: () => {
                getUser({ variables: { id } });
              },
              type: "ok",
            },
          ]);
        } else {
          Alert.alert("", "重設失敗，請再次嘗試。", [
            { text: "確定", type: "ok" },
          ]);
        }
      },
    }
  );
  useEffect(() => {
    if (Boolean(id)) {
      getUser({ variables: { id: Number(id) } });
    }
  }, [id]);

  function save(data) {
    let userInput = {
      ...data,
      gender: data.gender.value,
      identity: !!id ? userData?.user.identity : "STAFF",
      enabled: true,
    };
    delete userInput.joinTime;
    delete userInput.district;
    delete userInput.subdistrict;
    if (!!data.district) {
      userInput.address = {
        division: data.district,
        subdivision: data.subdistrict,
        address: data.address,
      };
    } else {
      delete userInput.address;
    }
    if (!!id) {
      userInput.id = id;
      updateUser({ variables: { userInput, console: true } });
    } else {
      userRegister({ variables: { userInput, console: true } });
    }
  }
  return (
    <Card>
      <LoadingModal
        loading={
          loading ||
          updateUserLoading ||
          registerLoading ||
          changeUserEnabledLoading ||
          resetPasswordLoading
        }
      />
      <CardContent>
        <Grid container spacing justifyContent="flex-end">
          {id && (
            <>
              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      color="primary"
                      onChange={() => {
                        setEnabled(!enabled);
                        changeUserEnabled({
                          variables: { id: Number(id), enabled },
                        });
                      }}
                    />
                  }
                  label="開通"
                />
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onPress={() => {
                    Alert.alert("通知", "確定要重設密碼？", [
                      {
                        text: "確定",
                        onPress: () => {
                          resetPassword({
                            variables: { id: Number(id), password: "12345678" },
                          });
                        },
                        type: "ok",
                      },
                      {
                        text: "取消",
                        type: "cancel",
                      },
                    ]);
                  }}
                >
                  重設密碼
                </Button>
              </Grid>
            </>
          )}
        </Grid>
        <Grid container spacing={1} justifyContent="space-evenly">
          <Grid item xs={12} sm={2}>
            <Typography color="primary">姓名</Typography>
            <Controller
              name="fullName"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">性別</Typography>
            <Controller
              name="gender"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControl size="small" variant="outlined" fullWidth>
                  <Select
                    items={genderArray}
                    value={value}
                    onChange={(e, v) => onChange(v)}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">帳號</Typography>
            <Controller
              name="username"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          {!id && (
            <Grid item xs={12} sm={2}>
              <Typography color="primary">密碼</Typography>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    className={classes.input}
                    type={"password"}
                    {...field}
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid container spacing={1} justifyContent="space-evenly">
          <Grid item xs={12} sm={2}>
            <Typography color="primary">分店</Typography>
            <Controller
              name="branch"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">部門</Typography>
            <Controller
              name="department"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">職位</Typography>
            <Controller
              name="position"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">傳真</Typography>
            <Controller
              name="fax"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} justifyContent="space-evenly">
          <Grid item xs={12} sm={2}>
            <Typography color="primary">手機</Typography>
            <Controller
              name="mobile"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">電話</Typography>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">信箱</Typography>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">生日</Typography>
            <Controller
              name="birthday"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DateInput
                  openTo="year"
                  value={value}
                  onChange={onChange}
                  format={"yyyy/MM/dd"}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} justifyContent="space-evenly">
          <Grid item xs={12} sm={2}>
            <Typography color="primary">縣市</Typography>
            <Controller
              name="district"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Mselect
                  native
                  value={divisions[value]?.zhTW}
                  classes={{ root: classes.select }}
                  disableUnderline
                  onChange={(e) => {
                    onChange(city[e.target.selectedIndex - 1]);
                    setSelectDistrict(
                      divisions[city[e.target.selectedIndex - 1]].subdivisions
                    );
                  }}
                >
                  <option disabled selected value="">
                    縣市
                  </option>
                  {Object.keys(divisions).map((item, index) => (
                    <option key={item}>{divisions[city[index]].zhTW}</option>
                  ))}
                </Mselect>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">區域</Typography>
            <Controller
              name="subdistrict"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Mselect
                  native
                  value={
                    divisions[getValues("district")]?.subdivisions[value]?.zhTW
                  }
                  classes={{ root: classes.select }}
                  disableUnderline
                  onChange={(e) => {
                    onChange(
                      Object.keys(selectDistrict)[e.target.selectedIndex - 1]
                    );
                  }}
                >
                  <option disabled selected value="">
                    鄉鎮/區域
                  </option>
                  {selectDistrict !== undefined
                    ? Object.keys(selectDistrict)
                        .map((item) => selectDistrict[item].zhTW)
                        .map((item, index) => (
                          <option key={item}>{item}</option>
                        ))
                    : null}
                </Mselect>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">地址</Typography>
            <Controller
              name="address"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="primary">加盟時間</Typography>
            <Controller
              name="joinTime"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DateInput
                  openTo="year"
                  value={value}
                  onChange={onChange}
                  format={"yyyy/MM/dd"}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardActions>
        <Grid container spacing={1} justify="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onPress={handleSubmit(save)}
            >
              儲存
            </Button>
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
}
